<template>
	<WebViewLayout v-if="isWebview" data-test="webview">
		<KeyTakeaways v-if="keyTakeaways.length > 0" :key-takeaways="keyTakeaways" />
		<RichTextComponent
			:blocks="bodyContent"
			class="body-lg-md wysiwyg-generated-content article-body investor-update-rich-text'"
		/>
	</WebViewLayout>
	<BaseLayout v-else :footer-full="true" :content-full-height="true" background-color-class="bg-color-white">
		<div class="container">
			<div class="grid-lg gutters-300-lg">
				<div class="col-8-md col-offset-2-md" data-test="investor-update-content">
					<BasePlaceHolder :line-count="4" :section-count="3" :placehold="loading">
						<AccountBreadcrumb v-if="isAuthenticated" :items="breadcrumbs" data-test="account-breadcrumb" />
						<div v-else-if="isPropertyUpdate" class="mb-300">
							<div
								class="module text-align-center footer banner-unauth"
								data-test="property-update-disclaimer"
							>
								<div class="module-section">
									<p class="body-lg-md body-md-smo">
										Every Fundrise portfolio aims to diversify with a broad mixture of properties.
										Here's an example of one deal that has powered investors'&nbsp;portfolios.
									</p>
								</div>
							</div>
						</div>
						<h1 class="heading-1 mt-50 mb-100-smo mb-200-md">
							{{ title }}
						</h1>
						<p
							v-if="subtitle"
							class="body-lg text-color-content-tertiary body-xl-md font-weight-lighter mt-100-md"
						>
							{{ subtitle }}
						</p>
						<p
							class="display-flex justify-content-space-between align-items-flex-start mt-100-md body-sm text-color-content-tertiary"
						>
							{{ date(displayDate) }}
						</p>

						<div v-if="mediaItems.length" class="mt-100 mt-150-md">
							<MediaGalleryPreview
								class="article-media-gallery"
								:investor-update-id="investorUpdateId"
								:caption="galleryCaption"
								:media-items="mediaItems"
							/>
						</div>

						<KeyTakeaways v-if="keyTakeaways.length > 0" :key-takeaways="keyTakeaways" />

						<RichTextComponent
							:blocks="bodyContent"
							class="mt-100 mt-150-md body-lg-md wysiwyg-generated-content article-body investor-update-rich-text"
						/>

						<div v-if="attachments.length > 0">
							<InvestorUpdateAttachments :attachments="attachments" />
						</div>

						<div v-if="associatedInvestorUpdates.length > 1">
							<InvestorUpdateTimeline
								:all-updates="associatedInvestorUpdates"
								:investor-update-id="investorUpdateId"
							/>
						</div>

						<TransitionCollapse>
							<div v-if="displayMicroFeedback" data-test="radio-feedback-wrapper" class="col-6-md mt-150">
								<h3 class="body-md font-weight-bold"
									>How would you rate your overall satisfaction with this experience?</h3
								>
								<MicroFeedbackRadios @reaction-selected="reactionSelected" />
							</div>
						</TransitionCollapse>

						<div v-if="assetElement">
							<h2 class="heading-5 mt-150"> More about this asset </h2>
							<div class="display-flex flex-direction-column mt-150">
								<AssetElementCard :asset-element="assetElement" :use-mobile-card="useMobileCard" />
							</div>
						</div>

						<RichTextComponent
							v-if="disclaimers.length"
							:blocks="disclaimers"
							class="content-block text-color-content-tertiary body-xs article-disclaimers pt-200"
						/>

						<section v-if="isPropertyUpdate"
							><div class="mt-300" data-test="property-update-cta">
								<div class="module text-align-center start-investing-module">
									<div class="module-section">
										<div class="heading-5 text-color-white">Ready to Invest?</div>
										<div class="body-lg-md mt-100">
											<BaseLink
												class="button button-primary"
												:link="{ router: 'start-investing' }"
												>View investment options</BaseLink
											>
										</div>
									</div>
								</div>
							</div>
						</section>
					</BasePlaceHolder>
				</div>
			</div>
		</div>
		<template #footer>
			<BasePlaceHolder :line-count="3" :placehold="loading" :class="{ container: loading }">
				<FooterFull :disclaimers="footerDisclaimers" />
			</BasePlaceHolder>
		</template>
	</BaseLayout>
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent, Ref, ref } from 'vue';
import { date, sanitizeNumbers } from '@filters/shared-filters';
import {
	InvestorUpdateMediaItem,
	InvestorUpdatePreview,
	InvestorUpdateView,
	InvestorUpdateViewAttachment
} from 'types/account';
import { useRoute, useRouter } from 'vue-router';
import { Asset } from 'types/assets';
import FooterFull from '@components/footer/footer-full.vue';
import { getInvestorUpdateView } from '@api/sanity/proxy/investor-updates';
import { Link } from 'types/layout';
import { logError } from '@utils/error-tracking';
import MediaGalleryPreview from '@components/account/media-gallery-preview.vue';
import { MicroFeedbackReactionType } from 'types/micro-feedback';
import RichTextComponent from '@components/sanity/rich-text-component.vue';
import { SanityRichTextBlock } from 'types/sanity';
import { setItemInSessionStorage } from '@utils/web-storage';
import { storageKeyLoginRedirect } from '@constants/storage-keys';
import TransitionCollapse from '@components/layout/transition-collapse.vue';
import { useAppStore } from '@stores/app';
import { useHead } from '@unhead/vue';
import { useSanityImage } from '@utils/composables/use-sanity-image';
import { useUserStore } from '@stores/user';

const appStore = useAppStore();
const userStore = useUserStore();
const route = useRoute();
const router = useRouter();
const investorUpdate: Ref<InvestorUpdateView | null> = ref(null);
const reaction: Ref<MicroFeedbackReactionType | null> = ref(null);
const isPropertyUpdate = ref(false);

const AccountBreadcrumb = defineAsyncComponent(() => import('@components/account/account-breadcrumb.vue'));
const AssetElementCard = defineAsyncComponent(() => import('@components/asset-element/asset-element-card.vue'));
const InvestorUpdateAttachments = defineAsyncComponent(
	() => import('@components/investor-update/investor-update-attachments.vue')
);
const InvestorUpdateTimeline = defineAsyncComponent(
	() => import('@components/investor-update/investor-update-timeline.vue')
);
const KeyTakeaways = defineAsyncComponent(() => import('@components/layout/key-takeaways.vue'));
const MicroFeedbackRadioFormModal = defineAsyncComponent(
	() => import('@components/micro-feedback/micro-feedback-radio-form-modal.vue')
);
const MicroFeedbackRadios = defineAsyncComponent(() => import('@components/micro-feedback/micro-feedback-radios.vue'));
const WebViewLayout = defineAsyncComponent(() => import('@views/webview/web-view-layout.vue'));

const investorUpdateId = computed((): string => {
	return route.params.investorUpdateId as string;
});

const loading = computed((): boolean => {
	return investorUpdate.value === null;
});

const isInvestor = computed((): boolean => {
	return userStore.isExistingInvestor;
});

const breadcrumbs = computed((): Array<Link> => {
	return [
		{
			text: 'Home',
			router: 'account-overview'
		},
		{
			text: 'Newsfeed',
			router: isInvestor.value ? 'newsfeed' : 'newsfeed-public'
		},
		{
			text: title.value,
			router: 'investor-update',
			params: { investorUpdateId: investorUpdateId.value }
		}
	];
});

const isAuthenticated = computed(() => appStore.isAuthenticated);

const isWebview = computed(() => appStore.isMobileWebview);

const title = computed((): string => {
	return investorUpdate.value?.title ?? '';
});

const subtitle = computed((): string | null => {
	return investorUpdate.value?.subtitle ?? null;
});

const displayDate = computed((): string => {
	return (
		(isPropertyUpdate.value ? investorUpdate.value?.leadUpdateDisplayDate : investorUpdate.value?.displayDate) ?? ''
	);
});

const galleryCaption = computed((): string => {
	return investorUpdate.value?.coverMedia?.caption ?? '';
});

const mediaItems = computed((): Array<InvestorUpdateMediaItem> => {
	return (
		investorUpdate.value?.coverMedia?.mediaItems?.map((item) => ({
			mediaItemType: item.mediaItemType,
			sourceUrl: useSanityImage(item.sourceUrl, 'highRes')!,
			thumbnailUrl: useSanityImage(item.mediaItemType === 'PHOTO' ? item.sourceUrl : item.thumbnailUrl, 'cover')!,
			vimeoUrl: item.vimeoUrl,
			caption: item.caption
		})) ?? []
	);
});

const bodyContent = computed((): Array<SanityRichTextBlock> => {
	return investorUpdate.value?.bodyContent ?? [];
});

const attachments = computed((): Array<InvestorUpdateViewAttachment> => {
	return investorUpdate.value?.attachments ?? [];
});

const associatedInvestorUpdates = computed((): Array<InvestorUpdatePreview> => {
	return investorUpdate.value?.associatedInvestorUpdates ?? [];
});

const assetElement = computed((): Asset | null => {
	return investorUpdate.value?.associatedAssets ? investorUpdate.value?.associatedAssets[0] : null;
});

const disclaimers = computed((): Array<SanityRichTextBlock> => {
	return investorUpdate.value?.disclaimers ?? [];
});

const keyTakeaways = computed(() => {
	return investorUpdate.value?.keyTakeaways ?? [];
});

const footerDisclaimers = computed((): Array<SanityRichTextBlock> => {
	return investorUpdate.value?.footerDisclaimer ?? [];
});

const displayMicroFeedback = computed((): boolean => {
	return (!isPropertyUpdate.value && investorUpdate.value?.displayMicroFeedbackPrompt) ?? false;
});

const useMobileCard = computed((): boolean => {
	return !appStore.isDesktop;
});

function buildPageMeta(): object {
	const customMeta = [];
	const metaTitle = investorUpdate.value ? investorUpdate.value.title : '';
	customMeta.push({ property: 'og:title', content: metaTitle });

	if (investorUpdate.value?.subtitle) {
		customMeta.push(
			{ name: 'description', content: investorUpdate.value.subtitle },
			{ property: 'og:description', content: investorUpdate.value.subtitle }
		);
	}

	if (investorUpdate.value && investorUpdate.value.thumbnailUrl) {
		customMeta.push({
			property: 'og:image',
			content: investorUpdate.value.thumbnailUrl
		});
	}

	return {
		title: metaTitle,
		meta: customMeta
	};
}

function openMicroFeedbackModal(): void {
	appStore.updateCurrentModal({
		modal: MicroFeedbackRadioFormModal,
		props: {
			microFeedbackEventType: 'INVESTOR_UPDATE',
			associatedEventId: investorUpdateId.value,
			preSelectedResponse: reaction.value
		}
	});
	if (investorUpdate.value) {
		investorUpdate.value.displayMicroFeedbackPrompt = false;
	}
}

function reactionSelected(selectedReaction: MicroFeedbackReactionType): void {
	reaction.value = selectedReaction;
	openMicroFeedbackModal();
}

async function setup(): Promise<void> {
	isPropertyUpdate.value = route.name === 'property-update';

	try {
		investorUpdate.value = await getInvestorUpdateView(investorUpdateId.value);
		if (isPropertyUpdate.value && !investorUpdate.value?.isLeadUpdate) {
			router.push({ name: '404' });
		}
		const metaInfo = buildPageMeta();
		useHead(metaInfo);

		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		const errorStatus = error.response?.status ?? '';
		const errorDescription = error?.response?.data?.errorDescription ?? '';
		const url = error.response?.config?.url ? sanitizeNumbers(error.response.config.url) : undefined;
		const errorContext = {
			key: 'Axios',
			data: {
				url,
				data: error.response?.data,
				status: errorStatus
			}
		};

		if (errorStatus === 401) {
			setItemInSessionStorage(storageKeyLoginRedirect, router.currentRoute.value.fullPath);
			await router.push({ name: 'login' });
			return;
		}

		if (errorStatus === 403) {
			logError('Auth investor update inaccessible, deleted, unpublished', 'info', errorContext);
			await router.push({ name: 'home' });
			return;
		}

		if (errorDescription === `No Investor Update with ID: ${investorUpdateId.value}`) {
			logError(
				'Auth investor update non-existent; Unauth investor update deleted, unpublished, or non-existent',
				'info',
				errorContext
			);
			await router.push({ path: '/404' });
			appStore.addToast({
				type: 'error',
				message: 'The investor update is no longer available.'
			});
			return;
		}

		logError('Investor update general error', 'info', errorContext);
		await router.push({ path: '/404' });
	}
}

setup();
</script>

<style lang="scss" scoped>
@use '../../styles/constants/colors.scss' as *;
@use '../../styles/utilities/respond-to.scss' as *;

.banner-unauth {
	box-shadow: none;
	background-color: gray(30);
}

.start-investing-module {
	box-shadow: none;
	background-color: hsl(210, 100%, 25%);
	background-image: linear-gradient(to right, hsl(210, 100%, 30%), hsl(210, 100%, 25%) 50%);
}

:deep(.sanity-rich-text-container > * + .sanity-rich-text-component) {
	margin-top: 1rem;

	@include respond-to(md) {
		margin-top: 2rem;
	}
}

:deep(.investor-update-rich-text li) {
	margin-left: 0.5rem;

	@include respond-to(md) {
		margin-left: 1rem;
	}
}
</style>
